import dayjs from 'dayjs'
import { forwardRef } from 'react'
import type { components } from '~/api/BackendClient/generated-schema'
import TrendingFlatIcon from '~/components/icons/TrendingFlatIcon'
import Logo from '~/components/Logo'

type RepresentativeImageResponse = components['schemas']['RepresentativeImageResponse']

interface ImageCardProps {
  image: RepresentativeImageResponse
}

function ImageCard({ image }: ImageCardProps) {
  return (
    <div className='flex flex-col gap-2 shrink-0'>
      <div className='flex flex-col gap-0.5'>
        <div className='font-semibold text-grey-dark text-xs leading-tight'>
          {image.plate_barcode}
        </div>
        <div className='text-grey-medium text-[11px]'>{image.well}</div>
        <div className='text-grey-medium text-[11px]'>
          {dayjs(image.observation_timestamp).format('MMM D, YYYY h:mm A')}
        </div>
        {image.confluence !== null && (
          <div className='text-grey-medium text-[11px]'>
            Confluence: {Math.round(image.confluence)}%
          </div>
        )}
      </div>
      <div className='w-[200px] h-[200px] rounded overflow-hidden border border-grey-light'>
        <img
          src={image.s3_url}
          alt={`${image.plate_barcode} ${image.well}`}
          className='w-full h-full object-cover'
          crossOrigin='anonymous'
        />
      </div>
    </div>
  )
}

function PassageArrow() {
  return (
    <div className='flex items-center justify-center px-2 self-end mb-[100px]'>
      <TrendingFlatIcon className='fill-grey-dark w-6 h-6' />
    </div>
  )
}

export interface CertificateOfAnalysisProps {
  images: RepresentativeImageResponse[]
}

/**
 * Certificate of Analysis component that displays representative images
 * in a filmstrip format with passage arrows between culture changes.
 * This component is designed to be captured and exported as an image.
 */
const CertificateOfAnalysis = forwardRef<HTMLDivElement, CertificateOfAnalysisProps>(
  ({ images }, ref) => {
    if (images.length === 0) {
      return null
    }

    // Determine where passages occur (when culture_id changes)
    const imagesWithPassages: {
      image: RepresentativeImageResponse
      showArrowAfter: boolean
    }[] = []

    for (let i = 0; i < images.length; i++) {
      const image = images[i]
      const nextImage = images[i + 1]
      // Show arrow if the next image has a different culture_id (indicating a passage)
      const showArrowAfter = nextImage && nextImage.culture_id !== image.culture_id
      imagesWithPassages.push({ image, showArrowAfter })
    }

    return (
      <div ref={ref} className='flex items-center justify-center p-4 bg-grey-lightest'>
        <div className='bg-white border-2 border-accent rounded-lg py-5 px-6 w-full'>
          <h1 className='font-semibold text-lg text-grey-dark mb-6'>
            Certificate of Analysis
          </h1>

          <div className='flex flex-row items-start gap-6 pb-2'>
            {imagesWithPassages.map(({ image, showArrowAfter }) => (
              <div
                key={image.image_id}
                className='flex flex-row items-center gap-4 shrink-0'
              >
                <ImageCard image={image} />
                {showArrowAfter && <PassageArrow />}
              </div>
            ))}
          </div>

          <div className='flex justify-end mt-4'>
            <div className='bg-grey-dark rounded px-3 py-1.5'>
              <Logo className='h-5' />
            </div>
          </div>
        </div>
      </div>
    )
  },
)

CertificateOfAnalysis.displayName = 'CertificateOfAnalysis'

export default CertificateOfAnalysis
